/**
 * Utility functions for formatting text in the application
 */

/**
 * Normalizes category and tag names by:
 * - Converting underscores and hyphens to spaces
 * - Properly capitalizing each word
 * - Handling special cases like "CBD", "THC", "1:1", etc.
 */
export function formatCategoryName(name: string): string {
  if (!name) return '';

  // Preserve ratio patterns like "1:1"
  if (/^\d+:\d+$/.test(name)) return name;

  const specialCases = new Set(["cbd", "thc", "cbn", "cbg"]);

  return name
    .replace(/[_-]/g, ' ')
    .split(' ')
    .map(word => {
      const lower = word.toLowerCase();
      return specialCases.has(lower)
        ? lower.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * NY Cannabis tax rate included in Dutchie API prices
 * The API returns prices with ~13% state tax baked in
 * Checkout adds additional local taxes (~4-5% more)
 */
export const DUTCHIE_TAX_RATE = 0.13;

/**
 * Formats a price for display (with tax included - as returned by API)
 */
export function formatPrice(price?: number): string {
  return typeof price === 'number' ? `$${price.toFixed(2)}` : '';
}

/**
 * Formats a price WITHOUT the baked-in tax (to match Dutchie checkout display)
 * Use this when you want prices to match what Dutchie checkout shows
 */
export function formatPricePreTax(price?: number): string {
  if (typeof price !== 'number') return '';
  const preTax = price / (1 + DUTCHIE_TAX_RATE);
  return `$${preTax.toFixed(2)}`;
}

/**
 * Removes the baked-in tax from a price (returns number)
 */
export function getPricePreTax(price: number): number {
  return price / (1 + DUTCHIE_TAX_RATE);
}

/**
 * Checks if two tags/categories are equivalent despite formatting differences
 */
export function areTagsEquivalent(tag1: string, tag2: string): boolean {
  if (!tag1 || !tag2) return false;

  const normalize = (str: string): string => {
    const cleaned = str.toLowerCase().replace(/[_\-\s]/g, '');

    const mappings: Record<string, string[]> = {
      vaporizers: ['vaporizer', 'vape', 'vapes', 'vapor'],
      prerolls: ['preroll', 'pre-roll', 'pre-rolls', 'pre_roll', 'pre_rolls'],
      concentrates: ['concentrate', 'wax', 'dabs', 'extract', 'extracts'],
      edibles: ['edible', 'food', 'gummies'],
      tinctures: ['tincture', 'drops', 'oil', 'oils'],
    };

    for (const [canonical, variants] of Object.entries(mappings)) {
      if (cleaned === canonical || variants.includes(cleaned)) {
        return canonical;
      }
    }

    return cleaned;
  };

  return normalize(tag1) === normalize(tag2);
}

/**
 * Get a URL-friendly version of a category name
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/\s+/g, '-')       // Replace spaces with dashes
    .replace(/[^\w-]+/g, '')    // Remove non-word characters
    .replace(/--+/g, '-')       // Collapse multiple dashes
    .replace(/^-+|-+$/g, '');   // Trim leading/trailing dashes
}
